import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';
import {
  nettoyageExpress,
  nettoyageApprofondi,
  nettoyageSupreme,
  polishCorrection,
  ceramiqueCarrosserie,
  ceramiqueOptions,
  supplements,
  serviceDomicile,
  cielEtoile,
} from '../../mocks/prestations';

export default function PrestationsPage() {
  const navigate = useNavigate();

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.louatiautocenter.ch';

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Nos Prestations – Detailing, Polish, Céramique | Louati AUTOCENTER Payerne",
      "description": "Découvrez toutes nos prestations d'esthétique automobile à Payerne : nettoyage express, approfondi, suprême, polish, correction peinture, céramique 2 à 8 ans, ciel étoilé. Réservation en ligne.",
      "url": `${siteUrl}/prestations`,
      "inLanguage": "fr-CH",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${siteUrl}/` },
          { "@type": "ListItem", "position": 2, "name": "Prestations", "item": `${siteUrl}/prestations` }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Esthétique automobile",
      "provider": {
        "@type": "AutomotiveBusiness",
        "name": "Louati AUTOCENTER Sàrl",
        "url": siteUrl,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Z.I. Champ Cheval 2",
          "addressLocality": "Payerne",
          "postalCode": "1530",
          "addressCountry": "CH"
        }
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Prestations Detailing",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Nettoyage Express", "description": "Lavage extérieur et intérieur rapide dès 69 CHF" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Nettoyage Approfondi", "description": "Nettoyage complet et minutieux intérieur/extérieur" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Nettoyage Suprême", "description": "Le summum du detailing pour un résultat exceptionnel" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Polish & Correction Peinture", "description": "Lustrage et correction peinture dès 299 CHF" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Protection Céramique", "description": "Protection longue durée 2 à 8 ans dès 800 CHF" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Ciel Étoilé", "description": "Installation fibres optiques dans l'habitacle" } }
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen bg-dark-bg">
      <SeoHead
        title="Nos Prestations – Detailing, Polish, Céramique | Louati AUTOCENTER Payerne"
        description="Découvrez toutes nos prestations d'esthétique automobile à Payerne : nettoyage express, approfondi, suprême, polish, correction peinture, céramique 2 à 8 ans, ciel étoilé. Réservation en ligne."
        keywords="detailing automobile Payerne, polish voiture, céramique carrosserie, nettoyage auto Suisse, ciel étoilé voiture"
        canonical={`${siteUrl}/prestations`}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/71b90a95f89fc66a43306b9c18f2c1d5.jpg"
            alt="Nos Prestations"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <h1 className="font-serif text-6xl lg:text-8xl font-bold mb-6">
            Nos Prestations
          </h1>
          <p className="text-xl lg:text-2xl font-light max-w-3xl mx-auto">
            Des services automobiles premium pour sublimer votre véhicule
          </p>
        </div>
      </section>

      {/* Note importante sur les durées */}
      <section className="py-8 bg-primary/10 border-y border-primary/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-primary/20 rounded-full flex-shrink-0">
              <i className="ri-time-line text-2xl text-primary"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-primary mb-2">
                À propos des durées de prestation
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Les durées indiquées sont des estimations moyennes. Selon l'état de votre véhicule, 
                la complexité du travail et les conditions spécifiques, <strong className="text-primary">la prestation peut prendre plus de temps</strong>. 
                Nous privilégions toujours la qualité du résultat plutôt que la rapidité d'exécution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Nettoyage Express */}
      <section className="py-24 bg-dark-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <div className="inline-block px-4 py-2 bg-primary/20 rounded-full mb-4">
              <span className="text-primary font-semibold text-sm uppercase tracking-wider">
                Catégorie A
              </span>
            </div>
            <h2 className="font-serif text-5xl font-bold text-primary mb-4">
              Nettoyage Express
            </h2>
            <p className="text-gray-400 text-lg">
              Pour un entretien rapide et efficace de votre véhicule
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {nettoyageExpress.map((service) => (
              <div
                key={service.id}
                className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl hover:shadow-primary/10 transition-shadow duration-300"
              >
                <h3 className="text-2xl font-bold text-primary mb-3">
                  {service.name}
                </h3>
                <p className="text-gray-400 mb-6">{service.description}</p>

                <div className="space-y-4">
                  {service.prices.map((price, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-3 border-b border-dark-border last:border-0"
                    >
                      <div>
                        <p className="font-medium text-gray-300">{price.size}</p>
                        <p className="text-sm text-gray-500 flex items-center mt-1">
                          <i className="ri-time-line mr-1"></i>
                          {price.duration}
                        </p>
                      </div>
                      <p className="text-2xl font-bold text-primary">
                        {price.price} CHF
                      </p>
                    </div>
                  ))}
                </div>

                <Link
                  to="/reservation"
                  className="mt-6 w-full block text-center px-6 py-3 bg-primary text-dark-bg rounded-lg font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap"
                >
                  Réserver
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nettoyage Approfondi */}
      <section className="py-24 bg-dark-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <div className="inline-block px-4 py-2 bg-accent-sky/20 rounded-full mb-4">
              <span className="text-accent-sky font-semibold text-sm uppercase tracking-wider">
                Catégorie B
              </span>
            </div>
            <h2 className="font-serif text-5xl font-bold text-primary mb-4">
              Nettoyage Approfondi
            </h2>
            <p className="text-gray-400 text-lg">
              Un nettoyage complet et minutieux pour un résultat impeccable
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {nettoyageApprofondi.map((service) => (
              <div
                key={service.id}
                className="bg-dark-bg border border-dark-border rounded-2xl p-8 hover:shadow-xl hover:shadow-primary/10 transition-shadow duration-300"
              >
                <h3 className="text-2xl font-bold text-primary mb-3">
                  {service.name}
                </h3>
                <p className="text-gray-400 mb-6">{service.description}</p>

                <div className="space-y-4">
                  {service.prices.map((price, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-3 border-b border-dark-border last:border-0"
                    >
                      <div>
                        <p className="font-medium text-gray-300">{price.size}</p>
                        <p className="text-sm text-gray-500 flex items-center mt-1">
                          <i className="ri-time-line mr-1"></i>
                          {price.duration}
                        </p>
                      </div>
                      <p className="text-2xl font-bold text-primary">
                        {price.price} CHF
                      </p>
                    </div>
                  ))}
                </div>

                <Link
                  to="/reservation"
                  className="mt-6 w-full block text-center px-6 py-3 bg-primary text-dark-bg rounded-lg font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap"
                >
                  Réserver
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nettoyage Suprême */}
      <section className="py-24 bg-dark-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <div className="inline-block px-4 py-2 bg-accent-gold/20 rounded-full mb-4">
              <span className="text-accent-gold font-semibold text-sm uppercase tracking-wider">
                Catégorie C
              </span>
            </div>
            <h2 className="font-serif text-5xl font-bold text-primary mb-4">
              Nettoyage Suprême
            </h2>
            <p className="text-gray-400 text-lg">
              Le summum du detailing pour un résultat exceptionnel
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {nettoyageSupreme.map((service) => (
              <div
                key={service.id}
                className="bg-gradient-to-br from-accent-gold/10 to-accent-gold/5 border-2 border-accent-gold/30 rounded-2xl p-8 hover:shadow-xl hover:shadow-accent-gold/10 transition-shadow duration-300"
              >
                <h3 className="text-3xl font-bold text-primary mb-3">
                  {service.name}
                </h3>
                <p className="text-gray-400 mb-8 text-lg">{service.description}</p>

                <div className="space-y-4">
                  {service.prices.map((price, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-4 border-b border-dark-border last:border-0"
                    >
                      <div>
                        <p className="font-semibold text-gray-300 text-lg">{price.size}</p>
                        <p className="text-sm text-gray-500 flex items-center mt-1">
                          <i className="ri-time-line mr-1"></i>
                          {price.duration}
                        </p>
                      </div>
                      <p className="text-3xl font-bold text-primary">
                        {price.price} CHF
                      </p>
                    </div>
                  ))}
                </div>

                <Link
                  to="/reservation"
                  className="mt-8 w-full block text-center px-6 py-4 bg-primary text-dark-bg rounded-lg font-semibold hover:bg-primary/90 transition-colors text-lg whitespace-nowrap"
                >
                  Réserver cette prestation
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Polish & Correction */}
      <section className="py-24 bg-dark-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="font-serif text-5xl font-bold text-primary mb-4">
              Polish & Polissage
            </h2>
            <p className="text-gray-400 text-lg">
              Redonnez tout son éclat à votre carrosserie
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {polishCorrection.map((service) => (
              <div
                key={service.id}
                className="bg-dark-bg border border-dark-border rounded-2xl p-8 hover:shadow-xl hover:shadow-primary/10 transition-shadow duration-300"
              >
                <h3 className="text-2xl font-bold text-primary mb-3">
                  {service.name}
                </h3>
                <p className="text-gray-400 mb-6">{service.description}</p>

                <div className="space-y-4">
                  {service.prices.map((price, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-3 border-b border-dark-border last:border-0"
                    >
                      <div>
                        <p className="font-medium text-gray-300">{price.size}</p>
                        <p className="text-sm text-gray-500 flex items-center mt-1">
                          <i className="ri-time-line mr-1"></i>
                          {price.duration}
                        </p>
                      </div>
                      <p className="text-2xl font-bold text-primary">
                        {price.price} CHF
                      </p>
                    </div>
                  ))}
                </div>

                <Link
                  to="/reservation"
                  className="mt-6 w-full block text-center px-6 py-3 bg-primary text-dark-bg rounded-lg font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap"
                >
                  Réserver
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Céramique */}
      <section className="py-24 bg-dark-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="font-serif text-5xl font-bold text-primary mb-4">
              Protection Céramique Carrosserie
            </h2>
            <p className="text-gray-400 text-lg">
              Une protection durable pour préserver la beauté de votre véhicule
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {ceramiqueCarrosserie.map((service) => (
              <div
                key={service.id}
                className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl hover:shadow-primary/10 transition-shadow duration-300"
              >
                <h3 className="text-2xl font-bold text-primary mb-3">
                  {service.name}
                </h3>
                <p className="text-gray-400 mb-6">{service.description}</p>

                <div className="space-y-4">
                  {service.prices.map((price, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-3 border-b border-dark-border last:border-0"
                    >
                      <div>
                        <p className="font-medium text-gray-300">{price.size}</p>
                        <p className="text-sm text-gray-500 flex items-center mt-1">
                          <i className="ri-time-line mr-1"></i>
                          Environ {price.duration}
                        </p>
                      </div>
                      <p className="text-2xl font-bold text-primary">
                        {price.price} CHF
                      </p>
                    </div>
                  ))}
                </div>

                <Link
                  to="/reservation"
                  className="mt-6 w-full block text-center px-6 py-3 bg-primary text-dark-bg rounded-lg font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap"
                >
                  Réserver
                </Link>
              </div>
            ))}
          </div>

          {/* Options Céramique */}
          <div className="mb-12">
            <h3 className="font-serif text-4xl font-bold text-primary mb-8">
              Options Céramique à la Carte
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ceramiqueOptions.map((option) => (
              <div
                key={option.id}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:shadow-lg hover:shadow-primary/10 transition-shadow duration-300"
              >
                <h4 className="text-xl font-bold text-primary mb-2">
                  {option.name}
                </h4>
                <p className="text-gray-400 text-sm mb-4">{option.description}</p>
                <p className="text-sm text-gray-500 flex items-center mb-3">
                  <i className="ri-time-line mr-2"></i>
                  Environ {option.duration}
                </p>
                <p className="text-2xl font-bold text-primary">
                  {option.priceLabel || `${option.price} CHF`}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-accent-gold/10 border border-accent-gold/30 rounded-xl p-6">
            <h4 className="font-bold text-primary mb-3 flex items-center">
              <i className="ri-information-line text-accent-gold mr-2 text-xl"></i>
              Informations importantes
            </h4>
            <ul className="space-y-2 text-gray-400">
              <li className="flex items-start">
                <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                <span>Les prix incluent : lavage + décontamination + préparation avant pose</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                <span>Supplément véhicule très marqué / vernis dur : + 100 à 400 CHF (sur devis)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-accent-gold mr-2 mt-1"></i>
                <span>Délai de cure : éviter pluie/lavage pendant 24-48h après application</span>
              </li>
              <li className="flex items-start">
                <i className="ri-time-line text-accent-gold mr-2 mt-1"></i>
                <span><strong>Durée variable :</strong> Les prestations peuvent prendre plus de temps selon l'état du véhicule</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Ciel Étoilé */}
      <section className="py-24 bg-dark-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <div className="inline-block px-4 py-2 bg-purple-500/20 rounded-full mb-4">
              <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">
                Personnalisation Premium
              </span>
            </div>
            <h2 className="font-serif text-5xl font-bold text-primary mb-4">
              Ciel Étoilé
            </h2>
            <p className="text-gray-400 text-lg">
              Transformez votre habitacle avec un ciel étoilé personnalisé en fibres optiques
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {cielEtoile.map((service) => (
              <div
                key={service.id}
                className="bg-dark-bg border border-dark-border rounded-2xl p-6 hover:shadow-xl hover:shadow-purple-500/10 transition-shadow duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-4">
                  <i className="ri-sparkling-2-line text-2xl text-purple-400"></i>
                </div>
                <h3 className="text-xl font-bold text-primary mb-2">
                  {service.stars} étoiles
                </h3>
                <p className="text-sm text-gray-400 mb-4">{service.description}</p>
                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <i className="ri-time-line mr-2"></i>
                  Environ {service.duration}
                </div>
                <div className="mb-4">
                  <p className="text-3xl font-bold text-primary mb-1">
                    {service.price} CHF
                  </p>
                  <p className="text-xs text-gray-500">
                    Acompte 30% à la réservation
                  </p>
                </div>
                <Link
                  to="/reservation"
                  className="w-full block text-center px-6 py-3 bg-primary text-dark-bg rounded-lg font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap"
                >
                  Réserver
                </Link>
              </div>
            ))}
          </div>

          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
            <h4 className="font-bold text-primary mb-3 flex items-center">
              <i className="ri-information-line text-purple-400 mr-2 text-xl"></i>
              Modalités de paiement
            </h4>
            <ul className="space-y-2 text-gray-400">
              <li className="flex items-start">
                <i className="ri-check-line text-purple-400 mr-2 mt-1"></i>
                <span><strong>30% d'acompte</strong> requis à la réservation pour confirmer votre commande</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-purple-400 mr-2 mt-1"></i>
                <span><strong>70% restants</strong> à régler après la réalisation de la prestation</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-purple-400 mr-2 mt-1"></i>
                <span>Installation professionnelle avec fibres optiques de haute qualité</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-purple-400 mr-2 mt-1"></i>
                <span>Effet personnalisable selon vos préférences (intensité, couleur)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-time-line text-purple-400 mr-2 mt-1"></i>
                <span><strong>Durée variable :</strong> La durée peut être plus longue selon la complexité de l'installation</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Suppléments */}
      <section className="py-24 bg-dark-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="font-serif text-5xl font-bold text-primary mb-4">
              Suppléments & Options
            </h2>
            <p className="text-gray-400 text-lg">
              Services additionnels pour répondre à tous vos besoins
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {supplements.map((supplement) => (
              <div
                key={supplement.id}
                className="bg-dark-bg border border-dark-border rounded-xl p-6 hover:shadow-lg hover:shadow-primary/10 transition-shadow duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary/20 rounded-lg mb-4">
                  <i className={`${supplement.icon} text-2xl text-primary`}></i>
                </div>
                <h4 className="text-lg font-bold text-primary mb-2">
                  {supplement.name}
                </h4>
                <p className="text-sm text-gray-400 mb-3">{supplement.description}</p>
                <p className="text-xl font-bold text-primary">
                  {supplement.price} CHF
                </p>
              </div>
            ))}
          </div>

          {/* Service à Domicile */}
          <div className="mb-12">
            <h3 className="font-serif text-4xl font-bold text-primary mb-8">
              Déplacement à Domicile
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {serviceDomicile.map((service) => (
              <div
                key={service.id}
                className="bg-dark-bg border border-dark-border rounded-xl p-6 hover:shadow-lg hover:shadow-primary/10 transition-shadow duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-accent-sky/20 rounded-lg mb-4">
                  <i className="ri-map-pin-line text-2xl text-accent-sky"></i>
                </div>
                <h4 className="text-xl font-bold text-primary mb-2">
                  {service.distance}
                </h4>
                <p className="text-2xl font-bold text-primary">
                  {service.priceLabel || `+ ${service.price} CHF`}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-dark-bg text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-5xl lg:text-6xl font-bold mb-6 text-primary">
            Prêt à sublimer votre véhicule ?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Réservez votre prestation en ligne et payez en toute sécurité
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/reservation"
              className="inline-flex items-center bg-primary text-dark-bg px-8 py-4 rounded-full hover:bg-primary/90 transition-all duration-300 whitespace-nowrap"
            >
              <span className="font-semibold mr-4">Réserver maintenant</span>
              <div className="w-10 h-10 flex items-center justify-center bg-dark-bg text-primary rounded-full">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 border border-dark-border rounded-lg font-semibold hover:bg-dark-card transition-colors whitespace-nowrap text-gray-300 cursor-pointer"
            >
              Retour
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
